import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Building2, Zap, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import EsidHelp from "@/components/EsidHelp";
import CompensationDisclosure from "@/components/CompensationDisclosure";
import UtilitySelector from "@/components/UtilitySelector";
import useCurrentState from "@/routes/useCurrentState";
import { hasFeature, isTrackActive, statePath, trackStatus } from "@/lib/stateRegistry";
import { SHOW_BUSINESS_TRACK } from "@/lib/displayFlags";
import { fetchPlans } from "@/lib/api";
import { formatLongDate } from "@/lib/planCosts";

const TRACK_META = {
  residential: { label: "Plan comparison", icon: HomeIcon, sub: "plans", desc: "Plan comparison is coming soon." },
  commercial: { label: "For your business", icon: Building2, sub: "commercial", desc: "Request quotes for your business." },
};

/** /{state}/. Residential and commercial are peers; lead_track (IL: commercial) goes first. Texas additionally
 * carries the utility (TDU) chooser below the track card (Design Specification Section 7): picking a utility
 * is the natural next step before plans, since Texas rates differ by delivery area and a ZIP code does not
 * reliably identify it. */
export default function StateLanding() {
  const state = useCurrentState();
  const live = isTrackActive(state, "residential") || isTrackActive(state, "commercial");
  // SEO_Copy_Rulings.md 2.7: title/description follow the live tools (ESID lookup / utility map), not the plan track.
  const toolsLive = live || hasFeature(state, "esid_lookup") || hasFeature(state, "tdu_content");
  const order = SHOW_BUSINESS_TRACK ? ["residential", "commercial"] : ["residential"];
  if (SHOW_BUSINESS_TRACK && state.lead_track === "commercial") order.reverse();
  const showUtilitySection = isTrackActive(state, "residential") && hasFeature(state, "tdu_content");

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16" data-testid="state-landing" data-state={state.slug}>
      <Seo
        title={toolsLive ? `${state.name} Electricity Help: ESID, Bills & Utilities` : `${state.name}: coming soon`}
        description={toolsLive ? `${state.name} tools for understanding your electricity: look up your ESID, decode your bill, and see which utility serves your address.` : `RateCurrent is not yet open in ${state.name}.`}
        path={statePath(state.slug)}
        noindex={!toolsLive}
      />
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-[#0A2540]">{state.name}</h1>
      <p className="mt-4 text-lg text-slate-600">{live ? "Choose where to start." : `We are not open in ${state.name} yet.`}</p>

      <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {order.map((track) => {
          const meta = TRACK_META[track];
          const Icon = meta.icon;
          const status = trackStatus(state, track);
          if (status === "disabled") return null;
          return (
            <li key={track} className="border border-slate-200 rounded-2xl p-6" data-testid={`landing-track-${track}`}>
              <Icon className="w-6 h-6 text-[#15803D]" aria-hidden="true" />
              <h2 className="font-display text-2xl font-bold mt-3 text-[#0A2540]">{meta.label}</h2>
              <p className="text-slate-600 mt-1">{status === "active" ? meta.desc : "Plan comparison is coming soon."}</p>
              <Link to={statePath(state.slug, meta.sub)} className="btn-green mt-5 inline-flex !py-2 !px-4 !text-sm" data-testid={`landing-cta-${track}`}>
                {status === "active" ? meta.label : "Get notified"}
              </Link>
            </li>
          );
        })}
      </ul>

      {hasFeature(state, "esid_help") && <EsidHelp />}

      {/* State-only tools: rendered only where the registry enables them (Texas today). */}
      {(hasFeature(state, "esid_lookup") || hasFeature(state, "tdu_content")) && (
        <div className="mt-10 flex flex-wrap gap-4" data-testid="state-tools">
          {hasFeature(state, "esid_lookup") && (
            <Link to={statePath(state.slug, "esid-lookup")} className="inline-flex items-center gap-2 underline" data-testid="tool-esid">
              <Zap className="w-4 h-4" aria-hidden="true" /> Find your ESID
            </Link>
          )}
          {hasFeature(state, "tdu_content") && (
            <Link to={statePath(state.slug, "utilities")} className="inline-flex items-center gap-2 underline" data-testid="tool-utilities">
              <MapPin className="w-4 h-4" aria-hidden="true" /> Your utility area
            </Link>
          )}
        </div>
      )}

      {showUtilitySection && <TexasUtilitySection state={state} />}
    </div>
  );
}

/**
 * Section 7 of the Design Specification: choose a utility area, see the verified-as-of date, and read the
 * compensation/scope disclosure and the Power to Choose reference, before moving on to plans. Additive to the
 * page above (not a replacement) so the existing residential/commercial track card and the `tool-utilities`
 * link stay exactly as they are for the route-matrix test suite.
 */
function TexasUtilitySection({ state }) {
  const navigate = useNavigate();
  const [tdu, setTdu] = useState(null);
  const [touched, setTouched] = useState(false);
  const [plans, setPlans] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    fetchPlans({ state: state.code })
      .then((d) => { if (!cancelled) setPlans(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setPlans([]); });
    return () => { cancelled = true; };
  }, [state.code]);

  const verifiedOn = useMemo(() => {
    if (!plans || plans.length === 0) return null;
    const latest = plans.reduce((max, p) => (p.verified_at && (!max || p.verified_at > max) ? p.verified_at : max), null);
    return formatLongDate(latest);
  }, [plans]);

  const continueToPlans = () => {
    if (!tdu) { setTouched(true); return; }
    navigate(`${statePath(state.slug, "plans")}?tdu=${tdu}`);
  };

  return (
    <section className="mt-14 pt-10 border-t border-slate-100" data-testid="texas-utility-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <h2 className="font-display text-2xl font-bold text-[#0A2540]">Choose your utility area</h2>
        {verifiedOn && (
          <span className="pill shrink-0" data-testid="landing-verified-chip">Rates verified as of {verifiedOn}</span>
        )}
      </div>
      <p className="mt-2 text-base text-slate-700 max-w-[65ch]" data-testid="landing-scope-sentence">
        We show plans RateCurrent has {verifiedOn ? `verified as of ${verifiedOn}` : "verified"}. We do not show every plan available at your address. Texas rates differ by delivery utility area, and a ZIP code does not reliably identify it, so choose from your bill below.
      </p>

      <div className="mt-6 max-w-[640px]">
        <UtilitySelector value={tdu} onChange={(id) => { setTdu(id); setTouched(false); }} />
        {touched && !tdu && (
          <p role="alert" className="text-sm text-[#B91C1C] mt-2" data-testid="utility-required-error">
            Choose your utility area to continue.
          </p>
        )}
        <button type="button" onClick={continueToPlans} className="btn-green mt-5 w-full sm:w-auto" data-testid="utility-continue">
          {tdu ? "See plans" : "Continue"}
        </button>
      </div>

      <p className="mt-8 text-slate-700 max-w-[65ch]">
        Your utility delivers electricity and responds to outages. Your provider sells you the electricity and sends the bill.
      </p>

      <CompensationDisclosure className="mt-6" />
    </section>
  );
}
